import _ from 'underscore';
import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Text } from '@fluentui/react';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { LanguageSelector } from '@bingads-webui-campaign-react/language-selector';
import { globalizationModelSetup } from '@bingads-webui-campaign/ads-globalization';
import { campaignLanguageStrings } from './constants';
import { getIntentLanguageStyles } from '../styles/get-intent-language-style';

export function SingleLanguageTarget(props) {
  const {
    selectedLanguage,
    setSelectedLanguage,
    permissions,
    locale,
    showLabel,
    additionalOptions,
  } = props;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getIntentLanguageStyles);
  const { LANGUAGES: supportedLanguages } = globalizationModelSetup({ permissions, culture: locale });

  const languages = useMemo(() =>
    _.chain(AdGroup.properties.Language.enum)
      .filter(language => _.contains(supportedLanguages, language))
      .sortBy()
      .unshift(...additionalOptions)
      .map(language => ({
        key: language,
        text: i18n.getString(campaignLanguageStrings[language]),
      }))
      .value(), [additionalOptions, i18n, supportedLanguages]);

  const allSupportedOptions = useMemo(() => ([...additionalOptions, ...supportedLanguages]), [additionalOptions, supportedLanguages]);
  const selectedLanguages = useMemo(() => _.filter([selectedLanguage], language => _.contains(allSupportedOptions, language)), [selectedLanguage, allSupportedOptions]);

  const onSelectedLanguagesChanged = useCallback((event, data) => {
    setSelectedLanguage(data.optionValue);
  }, [setSelectedLanguage]);

  const label = i18n.getString(_TL_('Select the language of the websites where you want your ads to appear.'));
  return (
    <div className={classNames('language-target', classes.intentTarget)}>
      <div className="select-language-label-container">
        {showLabel && <Text>{label}</Text>}
      </div>
      <LanguageSelector
        languages={languages}
        selectedLanguages={selectedLanguages}
        onSelectedLanguagesChanged={onSelectedLanguagesChanged}
        ariaLabel={label}
        multiSelect={false}
      />
    </div>
  );
}

SingleLanguageTarget.propTypes = {
  selectedLanguage: PropTypes.string,
  setSelectedLanguage: PropTypes.func.isRequired,
  locale: PropTypes.string.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  showLabel: PropTypes.bool,
  additionalOptions: PropTypes.arrayOf(PropTypes.string),
};

SingleLanguageTarget.defaultProps = {
  selectedLanguage: null,
  showLabel: true,
  additionalOptions: ['All'],
};
